/**
 * The external participant portal.
 *
 * A second audience with a second front door: a client who was in a meeting,
 * following a link from an email, with no account here and no Clerk session.
 * Everything below therefore talks to same-origin `/api/portal/*` — never to
 * the backend directly — because the session token lives in an httpOnly cookie
 * this page cannot read. See `portal-proxy.mjs` for why.
 *
 * That also means none of `src/lib/api.ts` applies: no bearer header, no
 * `VITE_API_URL`. Sharing that client would have quietly attached a Clerk token
 * to portal requests, or attached nothing and looked like it worked.
 */

export interface PortalMeeting {
  id: string
  title: string
  startedAt: string | null
  durationSeconds: number | null
  participantCount: number
  summary: string | null
  hasTranscript: boolean
}

export interface PortalGroup {
  id: string
  name: string
  code: string | null
  meetings: PortalMeeting[]
}

export interface PortalOverview {
  email: string
  groups: PortalGroup[]
  /** Meetings shared directly, outside any space. */
  direct: PortalMeeting[]
  total: number
}

export interface PortalNote {
  id: string
  title: string
  body: string
}

export interface PortalMeetingDetail extends PortalMeeting {
  participants: string[]
  transcript: string | null
  notes: PortalNote[]
  /** Presigned for an hour by the backend, so it is fetched with the meeting. */
  mediaUrl: string | null
  /** Audio-only meetings are a third of them; they still get a player. */
  mediaKind: 'video' | 'audio' | null
}

/** A 401 here means "the link expired", which the portal renders as sign-in. */
export class PortalUnauthorized extends Error {}

async function portal<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api/portal/${path}`, {
    ...init,
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
  })
  if (response.status === 401) throw new PortalUnauthorized('Your link has expired.')
  const body = (await response.json().catch(() => ({}))) as Record<string, unknown> & { error?: string }
  if (!response.ok) throw new Error(body.error ?? 'The portal is unavailable right now.')
  return body as T
}

interface WireMeeting {
  id: string
  title: string | null
  started_at: string | null
  duration_seconds: number | null
  participant_count: number
  summary: string | null
  has_transcript: boolean
}

function toMeeting(wire: WireMeeting): PortalMeeting {
  return {
    id: wire.id,
    title: wire.title ?? 'Meeting',
    startedAt: wire.started_at,
    durationSeconds: wire.duration_seconds,
    participantCount: wire.participant_count,
    summary: wire.summary,
    hasTranscript: wire.has_transcript,
  }
}

/* ---------- Signing in ---------- */

/**
 * Ask for a link.
 *
 * Always resolves, whatever the address. Telling an unknown address that it is
 * unknown turns this box into a way to ask whether a given person was in any of
 * the firm's meetings.
 */
export async function requestLoginLink(email: string): Promise<void> {
  await portal('request-login', {
    method: 'POST',
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  })
}

/**
 * Trade the emailed token for a session.
 *
 * The session itself never arrives here — the proxy strips it out of the
 * response and puts it in the cookie, so this resolves with nothing and the
 * next request is simply authenticated.
 */
export async function exchangeLoginToken(token: string): Promise<void> {
  await portal('exchange-link', { method: 'POST', body: JSON.stringify({ token }) })
}

export async function portalLogout(): Promise<void> {
  await portal('logout', { method: 'POST' })
}

/* ---------- Reading ---------- */

export async function getPortalOverview(): Promise<PortalOverview> {
  const body = await portal<{
    email: string
    spaces: Array<{ id: string; name: string; code: string | null; meetings: WireMeeting[] }>
    direct_meetings: WireMeeting[]
    total_meetings: number
  }>('overview')

  return {
    email: body.email,
    groups: body.spaces.map((space) => ({
      id: space.id,
      name: space.name,
      code: space.code,
      meetings: space.meetings.map(toMeeting),
    })),
    direct: body.direct_meetings.map(toMeeting),
    total: body.total_meetings,
  }
}

export async function getPortalMeeting(meetingId: string): Promise<PortalMeetingDetail> {
  const body = await portal<
    WireMeeting & {
      participants: Array<{ name: string }>
      transcript: string | null
      notes: Array<{ id: string; title: string; content_md?: string | null }>
      media_url?: string | null
      media_kind?: 'video' | 'audio' | null
      video_url?: string | null
    }
  >(`meetings/${encodeURIComponent(meetingId)}`)

  // `video_url` is the older field and still the only one a backend that has
  // not shipped `media_url` yet will send, so it is read as the fallback.
  const mediaUrl = body.media_url ?? body.video_url ?? null

  return {
    ...toMeeting(body),
    participants: body.participants.map((person) => person.name),
    transcript: body.transcript,
    notes: (body.notes ?? []).map((note) => ({
      id: note.id,
      title: note.title,
      body: note.content_md ?? '',
    })),
    mediaUrl,
    mediaKind: body.media_kind ?? (mediaUrl ? 'video' : null),
  }
}

/* ---------- Asking ---------- */

/**
 * A meeting the portal answer may cite, as `[[1]]`.
 *
 * The number is the position in this conversation's transcripts, not the
 * meeting's own id — the portal is read from outside the organisation, and an
 * org-wide consecutive number would say how much has been recorded.
 */
export interface PortalSource {
  ref: string
  meeting_id: string
  title: string
}

/**
 * Whether these transcripts still fit in one conversation.
 *
 * Asked before a meeting joins the selection rather than after the question is
 * typed: the failure is "this selection is too big", and a reader who learns
 * that from a rejected answer has already lost the question they wrote.
 */
export async function checkChatContext(
  meetingIds: string[]
): Promise<{ fits: boolean; inputTokens: number; maxInputTokens: number }> {
  const body = await portal<{ fits: boolean; input_tokens: number; max_input_tokens: number }>(
    'chat/context-check',
    { method: 'POST', body: JSON.stringify({ meeting_ids: meetingIds }) }
  )
  return { fits: body.fits, inputTokens: body.input_tokens, maxInputTokens: body.max_input_tokens }
}

export type PortalChatEvent =
  | { type: 'context'; sources: PortalSource[] }
  | { type: 'delta'; text: string }
  | { type: 'done' }
  | { type: 'error'; code: string; message: string }

/**
 * The portal's own assistant.
 *
 * Deliberately NOT the notes agent: that one searches everything the asker may
 * read and decides for itself which meetings to open. Here the meetings are
 * chosen by the reader and passed in, and the service answers from those
 * transcripts alone — a client asking about their own calls must not be able to
 * widen the question into the firm's other meetings.
 */
export async function* portalChat(
  input: { messages: Array<{ role: 'user' | 'assistant'; content: string }>; meetingIds: string[] },
  signal: AbortSignal
): AsyncGenerator<PortalChatEvent> {
  const response = await fetch('/api/portal/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages: input.messages, meeting_ids: input.meetingIds }),
    signal,
  })
  if (response.status === 401) throw new PortalUnauthorized('Your link has expired.')
  if (!response.ok || !response.body) throw new Error('The assistant is unavailable right now.')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { done, value } = await reader.read()
    buffer += decoder.decode(value, { stream: !done })

    let boundary = buffer.indexOf('\n\n')
    while (boundary !== -1) {
      const block = buffer.slice(0, boundary)
      buffer = buffer.slice(boundary + 2)
      const data = block
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n')
      if (data) {
        try {
          yield JSON.parse(data) as PortalChatEvent
        } catch {
          // A truncated event is not worth failing the answer over.
        }
      }
      boundary = buffer.indexOf('\n\n')
    }

    if (done) break
  }
}
